/**
 * EmailDocumentationPage Component
 * AIDLC Administrative Backend
 *
 * Documentation for the Email Management module: the portal sends only as agents@ldaja.com and reads no
 * mailbox or OneDrive (KD-1 E4, KD-1b).
 *
 * Structure:
 * - Overview
 * - Signature Guide (template fields, preview, branding)
 * - How email is sent (the three modes)
 * - Troubleshooting
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../shared/Card';
import { Badge } from '../shared/Badge';

// ─── Types ────────────────────────────────────────────────────────

interface Section {
  id: string;
  title: string;
  icon: React.ReactNode;
}

// ─── Constants ────────────────────────────────────────────────────

const SECTIONS: Section[] = [
  {
    id: 'overview',
    title: 'Overview',
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
      </svg>
    ),
  },
  {
    id: 'signature',
    title: 'Signature',
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
      </svg>
    ),
  },
  {
    id: 'permissions',
    title: 'How Email Is Sent',
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
      </svg>
    ),
  },
  {
    id: 'troubleshooting',
    title: 'Troubleshooting',
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path d="M11.42 15.17l-4.655 5.653a2.548 2.548 0 11-3.586-3.586l5.653-4.655m0 0a3.75 3.75 0 115.304-5.304m-5.304 5.304l5.304-5.304" />
      </svg>
    ),
  },
];

// ─── Section Content Components ───────────────────────────────────

function OverviewSection() {
  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600 leading-relaxed">
        The portal sends its own email (spend-cap alerts, notifications and automation rules) as
        agents@ldaja.com. It does not read any mailbox or OneDrive. The page at{' '}
        <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs font-mono">/email</code> sets the
        signature added to that mail.
      </p>

      <Card title="Access Requirements" subtitle="Who can use this feature">
        <div className="space-y-2 text-xs text-gray-600">
          <div className="flex items-center gap-3">
            <Badge variant="info">Role</Badge>
            <span>Admin or Operator role required to change signatures or send</span>
          </div>
          <div className="flex items-center gap-3">
            <Badge variant="purple">Persona</Badge>
            <span>Visible under the <strong>Ops</strong> persona in the sidebar navigation</span>
          </div>
        </div>
      </Card>
    </div>
  );
}

function SignatureSection() {
  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600 leading-relaxed">
        The Signature tab lets you configure the LDAJA-branded email signature that is
        automatically appended to every outgoing reply. The signature follows the brand
        guidelines with the LDAJA logo and &quot;BUILD SOMETHING BETTER&quot; tagline.
      </p>

      <Card title="Signature Fields" subtitle="What each field controls">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-gray-50">
                <th className="text-left px-3 py-2 font-semibold text-gray-700">Field</th>
                <th className="text-left px-3 py-2 font-semibold text-gray-700">Description</th>
                <th className="text-left px-3 py-2 font-semibold text-gray-700">Default</th>
              </tr>
            </thead>
            <tbody>
              {[
                { field: 'Valediction', desc: 'The sign-off line above your name (e.g., "Onward and upward,")', default: 'Onward and upward,' },
                { field: 'Signer Name', desc: 'The name displayed in the signature', default: 'Dan' },
                { field: 'Signer Title', desc: 'Job title or role', default: 'CEO' },
                { field: 'Signer Email', desc: 'Email address displayed in the signature', default: 'agents@ldaja.com' },
                { field: 'Tagline', desc: 'Brand tagline below the signature', default: 'BUILD SOMETHING BETTER' },
              ].map((row) => (
                <tr key={row.field} className="border-b border-gray-50">
                  <td className="px-3 py-2 font-medium text-gray-700">{row.field}</td>
                  <td className="px-3 py-2 text-gray-500">{row.desc}</td>
                  <td className="px-3 py-2 font-mono text-gray-400">{row.default}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Card title="Signature Layout">
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-100 space-y-3">
          <div className="text-xs text-gray-500 italic">Onward and upward,</div>
          <div>
            <div className="text-sm font-bold text-ldaja-black">Dan</div>
            <div className="text-xs text-gray-500">CEO</div>
            <div className="text-xs text-ldaja-blue">agents@ldaja.com</div>
          </div>
          <div className="border-t-2 border-ldaja-blue pt-3">
            <div className="text-[10px] font-bold tracking-[2px] text-ldaja-black">
              BUILD SOMETHING BETTER
            </div>
          </div>
        </div>
        <p className="text-[10px] text-gray-400 mt-3">
          Changes save immediately and are reflected in the live preview. The signature persists across server restarts.
        </p>
      </Card>
    </div>
  );
}

function PermissionsSection() {
  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600 leading-relaxed">
        The sender is always agents@ldaja.com. It cannot be changed, and there is no client secret.
      </p>
      <Card title="Where the portal runs" subtitle="How mail is sent depends on the server">
        <div className="space-y-2 text-xs text-gray-600">
          <p><strong>Production (ldaja.ai):</strong> the web app&apos;s managed identity sends through Microsoft Graph.
            Exchange allows that identity to send as agents@ldaja.com only.</p>
          <p><strong>The PC:</strong> the reviewed LDAJA Agents mail script sends, signed in with its certificate.
            It mails @ldaja.com addresses only.</p>
          <p><strong>Anywhere else:</strong> no email is sent. In-app notifications still show.</p>
        </div>
      </Card>
    </div>
  );
}

function TroubleshootingSection() {
  return (
    <div className="space-y-4">
      <Card title="Common Issues">
        <div className="space-y-4">
          {[
            {
              problem: 'Settings shows Email as off',
              cause: 'This server is not on App Service, and the PC mail script is not set up.',
              fix: 'Ask the operator to set up the PC mail script, or rely on in-app notifications on this server.',
            },
            {
              problem: 'Production mail is not arriving',
              cause: 'Exchange has not yet allowed the web app to send as agents@ldaja.com, so it refuses each send.',
              fix: 'An Exchange administrator allows the web app to send as agents@ldaja.com only. The server log names the refusal.',
            },
            {
              problem: 'Mail from the PC does not reach an outside address',
              cause: 'The PC mail script sends to @ldaja.com addresses only.',
              fix: 'Use an @ldaja.com address for alerts.',
            },
          ].map((item, idx) => (
            <div key={idx} className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="px-4 py-2 bg-gray-50 border-b border-gray-100">
                <span className="text-xs font-semibold text-gray-800">{item.problem}</span>
              </div>
              <div className="p-3 space-y-2">
                <div>
                  <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Cause</span>
                  <p className="text-xs text-gray-600 mt-0.5">{item.cause}</p>
                </div>
                <div>
                  <span className="text-[10px] font-semibold text-green-600 uppercase tracking-wider">Fix</span>
                  <p className="text-xs text-gray-600 mt-0.5">{item.fix}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

// ─── Section Renderer ─────────────────────────────────────────────

const SECTION_COMPONENTS: Record<string, () => JSX.Element> = {
  overview: OverviewSection,
  signature: SignatureSection,
  permissions: PermissionsSection,
  troubleshooting: TroubleshootingSection,
};

// ─── Main Component ───────────────────────────────────────────────

export function EmailDocumentationPage() {
  const [activeSection, setActiveSection] = useState('overview');
  const navigate = useNavigate();

  const ActiveContent = SECTION_COMPONENTS[activeSection];

  return (
    <div className="space-y-6 max-w-5xl">
      {/* Page Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <button
              onClick={() => navigate('/docs')}
              className="text-xs text-ldaja-blue hover:underline"
            >
              Documentation
            </button>
            <span className="text-xs text-gray-400">/</span>
            <span className="text-xs text-gray-500">Email Management</span>
          </div>
          <h1 className="text-xl font-bold text-ldaja-black">Email Management Guide</h1>
          <p className="text-sm text-gray-500 mt-1">
            How the portal sends email, and how to set its signature
          </p>
          <div className="flex items-center gap-3 mt-2 flex-wrap">
            <Badge variant="success" dot>Sends as agents@ldaja.com</Badge>
          </div>
        </div>
        <div className="text-right">
          <p className="text-[10px] text-gray-400">Last Updated</p>
          <p className="text-xs font-medium text-gray-600">2026-02-13</p>
          <p className="text-[10px] text-gray-400 mt-1">Authors</p>
          <p className="text-xs text-gray-600">Dan (Ops), Maya (UI), Elena (Docs)</p>
        </div>
      </div>

      {/* Section Navigation */}
      <div className="flex gap-1 border-b border-gray-200 overflow-x-auto">
        {SECTIONS.map((section) => (
          <button
            key={section.id}
            onClick={() => setActiveSection(section.id)}
            className={`flex items-center gap-2 px-3 py-2 text-xs font-medium border-b-2 transition-colors whitespace-nowrap ${
              activeSection === section.id
                ? 'border-ldaja-blue text-ldaja-blue'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            {section.icon}
            {section.title}
          </button>
        ))}
      </div>

      {/* Section Content */}
      <div>
        {ActiveContent && <ActiveContent />}
      </div>

      {/* Footer */}
      <div className="text-center py-4 border-t border-gray-100">
        <p className="text-[10px] text-gray-400">
          Email Management feature implemented Feb 2026. For issues, use the Chat Interface or contact Dan (Ops).
        </p>
      </div>
    </div>
  );
}
